import { lazy, Suspense, useEffect, useState } from "react";
import { configureAmplify } from "./utils/config/amplifyConfig";
import { BrowserRouter, createRoutesFromChildren, matchRoutes, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { RecoilRoot } from "recoil";
import RecoilNexus from "recoil-nexus";
import mixpanel from "mixpanel-browser";
import { HomeScrollProvider } from "./contexts/HomeScrollContext";
//load css
import "./App.css";
// import "swiper/swiper-bundle.min.css";
// import "swiper/swiper.min.css";
import "swiper/css/bundle";
import "swiper/css";
import "react-phone-number-input/style.css";
import "./components/navbar/mobileNavbar/mobileNavbar.css";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import "./pages/createListing/subPages/googleAutoComplete.css";
import "./pages/yourListing/yourStyling.css";

// Components
import ProtectedRoute from "./ProtectedRoutes";
import ScrollToTop from "./scrollToTop";
import Navbar from "./components/navbar/desktopNavbar/navbar";
import Authbar from "./components/auth/authbar";
import LoadingFullPageModal from "./components/loadingFullPageModal/loadingFullPageModal";

import { initLogin } from "./utils/auth/initLogin";
import { useCookies } from "react-cookie";

// Initialize currency detection early
import "./utils/userCurrencyUtils";

import ReferralPage from "./pages/referralPage/referralPage";
// Setup Sentry Error Logging and Monitoring
import * as Sentry from "@sentry/react";
import CheckGuestMode from "./components/checkGuestMode/CheckGuestMode";
import ErrorFallbackPage from "./components/errorFallbackPage/errorFallbackPage";

import { HelmetProvider } from "react-helmet-async";
import CatchGoogleLoginErr from "./components/auth/catchGoogleLoginErr";
import GoogleAuthRedirectHandler from "./components/googleAuthRedirectHandler/googleAuthRedirectHandler";
import CatchHospitableCode from "./components/catchHospitableCode/catchHospitableCode";
import VerificationFailedPopup from "./components/verificationFailedPopup/verificationFailedPopup";
import HostReferralPage from "./pages/referralPage/hostReferralPage";
import GoogleRedirectPage from "./pages/googleRedirectPage/googleRedirectPage";
import MobileNavbar from "./components/navbar/mobileNavbar/mobileNavbar";
import MainSidebarLayout from "@/components/Sidebar/MainSidebarLayout/MainSidebarLayout";
import ThemeColorManager from "./components/ThemeColorManager";
import { ToastProvider } from "./components/toast/ToastContext";
import AdminRouteGuard from "./components/admin/AdminRouteGuard";
import { SafeAreaProvider, SafeAreaTop, SafeAreaBottom, SafeAreaContent } from "./components/SafeAreaProvider";
import "./components/SafeAreaProvider.css";
import { SafeAreaTest } from "./components/SafeAreaTest";

Sentry.init({
    dsn: "https://ad815304989401de68858c7284549007@o4507525141430272.ingest.us.sentry.io/4507525143068672",
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    environment: process.env.REACT_APP_SENTRY_ENV,
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
});

// Lazy load components
const Home = lazy(() => import("./pages/newHome/home"));
const HostLandingPage = lazy(() => import("./pages/hostLandingPage/hostLandingPage"));
const Settings = lazy(() => import("./pages/settings/settings"));
const PayoutSettings = lazy(() => import("./pages/payoutSettings/payoutSettings"));
const PaymentSettings = lazy(() => import("./pages/paymentSettings/paymentSettings"));
const Profile = lazy(() => import("./pages/profile/profile"));
const HostProfile = lazy(() => import("./pages/hostProfile/profile"));
const Payouts = lazy(() => import("./pages/payouts/payouts"));
const Conversation = lazy(() => import("./pages/conversation/conversation"));
const HostConversation = lazy(() => import("./pages/hostConversation/hostConversation"));
const EditProfile = lazy(() => import("./pages/editProfile/editProfile"));
const EditListing = lazy(() => import("./pages/editListing/editListing"));
const ViewListing = lazy(() => import("./pages/viewListing/viewListing"));

const YourStays = lazy(() => import("./pages/yourStays/yourStays"));
const YourInbox = lazy(() => import("./pages/yourInbox/yourInbox"));
const HostInbox = lazy(() => import("./pages/hostInbox/hostInbox"));
const YourBooking = lazy(() => import("./pages/yourBooking/yourBooking"));
const ReviewStay = lazy(() => import("./pages/reviewStay/reviewStay"));
const ReviewGuest = lazy(() => import("./pages/reviewGuest/reviewGuest"));
const SearchListing = lazy(() => import("./pages/searchListing/searchListing"));
const YourListings = lazy(() => import("./pages/yourListing/yourListing"));
const YourCalendar = lazy(() => import("./pages/newCalendar/yourCalendar"));
const SavedListings = lazy(() => import("./pages/savedListings/savedListings"));
const BookingHostReceipt = lazy(() => import("./pages/bookingHostReceipt/bookingHostReceipt"));
const CreateListing = lazy(() => import("./pages/createListing/createListing"));
const CreateBooking = lazy(() => import("./pages/createBooking/createBooking"));
const CreateOrder = lazy(() => import("./pages/createOrder/createOrder"));
const ChangeBooking = lazy(() => import("./pages/changeBooking/changeBooking"));
const LoginAndSecurity = lazy(() => import("./pages/security/loginAndSecurity"));
const Dashboard = lazy(() => import("./pages/dashboard/dashboard"));
const BookingRequest = lazy(() => import("./pages/bookingRequest/bookingRequest"));
const ManagePayments = lazy(() => import("./pages/managePayments/managePayments"));
const PaymentDetails = lazy(() => import("./pages/paymentDetails/paymentDetails"));
const AllBookings = lazy(() => import("./pages/allBookings/allBookings"));
const AllOrders = lazy(() => import("./pages/allOrders/allOrders"));
const PrivacyPolicy = lazy(() => import("./pages/privacyPolicy/privacyPolicy"));
const PaymentPolicy = lazy(() => import("./pages/paymentPolicy/paymentPolicy"));
const GeneralPolicy = lazy(() => import("./pages/generalPolicy/generalPolicy"));
const IntegrationResult = lazy(() => import("./pages/integrationResult/integrationResult"));
const Extras = lazy(() => import("./pages/extras/extras"));
const CreateExtras = lazy(() => import("./pages/createExtras/createExtras"));
const EditExtra = lazy(() => import("./pages/editExtra"));
const Insights = lazy(() => import("./pages/insights/insights"));
const Tools = lazy(() => import("./pages/tools/tools"));
const SharePage = lazy(() => import("./pages/s/share"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminBookingDetails = lazy(() => import("./pages/admin/AdminBookingDetails"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetails = lazy(() => import("./pages/admin/AdminUserDetails"));
const AdminListings = lazy(() => import("./pages/admin/AdminListings"));
const AdminListingDetails = lazy(() => import("./pages/admin/AdminListingDetails"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminCouponDetails = lazy(() => import("./pages/admin/AdminCouponDetails"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminVerifications = lazy(() => import("./pages/admin/AdminVerifications"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const AdminEmailTemplate = lazy(() => import("./pages/admin/AdminEmailTemplate"));
const CollectionPage = lazy(() => import("./pages/collection/collection"));

declare const window: any;

const mixpanelToken: string = process.env.REACT_APP_MIXPANEL_TOKEN || "";
mixpanel.init(mixpanelToken);

if (process.env.REACT_APP_ENV === "production") {
    console.log = function () {};
    console.warn = function () {};
}

export default function App() {
    const [fullPageLoading, setFullPageLoading] = useState<boolean>(true);
    const [loggingIn, setLoggingIn] = useState<boolean>(true);
    const [, setCookie] = useCookies(["referralCode"]);
    const [integrationSuccess, setIntegrationSuccess] = useState<boolean | null>(null);

    useEffect(() => {
        // This will apply the amplify configuration
        configureAmplify();
        const initLoginFunc = async () => {
            await initLogin();
            setFullPageLoading(false);
            setLoggingIn(false);
        };
        initLoginFunc();
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const referralCode = urlParams.get("ref");

        if (referralCode) {
            setCookie("referralCode", referralCode, { path: "/" });
        }
    }, [setCookie]);

    const urlNeedLoading = (pathname: string) => {
        return (
            pathname !== "/" &&
            !pathname.includes("/listing") &&
            !pathname.includes("/search") &&
            !pathname.includes("/privacy-policy") &&
            !pathname.includes("/payment-policy") &&
            !pathname.includes("/terms-and-conditions") &&
            !pathname.includes("/list-your-place") &&
            !pathname.includes("/refer") &&
            !pathname.includes("/google-redirect") &&
            !pathname.includes("/book") &&
            !pathname.includes("/admin")
        );
    };

    return (
        <>
            <Sentry.ErrorBoundary fallback={<ErrorFallbackPage />}>
                <HelmetProvider>
                    <RecoilRoot>
                        <RecoilNexus />
                        <ToastProvider>
                            <SafeAreaProvider>
                                <HomeScrollProvider>
                                    <BrowserRouter>
                                    {fullPageLoading && urlNeedLoading(window.location.pathname) ? (
                                        <LoadingFullPageModal reverse={false} guestMode={true} />
                                    ) : (
                                        <>
                                            <ThemeColorManager />
                                            <CheckGuestMode />
                                            <SafeAreaTop>
                                                <Navbar loggingIn={loggingIn} showFullPageLoading={setFullPageLoading} />
                                            </SafeAreaTop>
                                            <CatchGoogleLoginErr />
                                            <CatchHospitableCode onSuccess={setIntegrationSuccess} />
                                            <Authbar />
                                            <GoogleAuthRedirectHandler />
                                            <ScrollToTop />
                                            <VerificationFailedPopup />
                                            <SafeAreaContent>
                                                <MobileNavbar />
                                                <Routes>
                                            {/* FIXME: This is a hack. */}
                                            {/* We put nesting router in here, but url is not nesting! */}
                                            <Route
                                              path="/"
                                              element={
                                                  <MainSidebarLayout />
                                              }>
                                                <Route
                                                  path="/"
                                                  element={
                                                    <Suspense fallback={<></>}>
                                                        <Home />
                                                    </Suspense>}
                                                />
                                                
                                                <Route
                                                  path="/saved/:id?"
                                                  element={
                                                      <Suspense fallback={<></>}>
                                                          <SavedListings />
                                                      </Suspense>
                                                  }
                                                />
                                                
                                                <Route
                                                  path="/trips/:id?"
                                                  element={
                                                      <Suspense fallback={<></>}>
                                                          <YourStays />
                                                      </Suspense>
                                                  }
                                                />
                                                
                                                <Route
                                                  path="/messages/:id"
                                                  element={
                                                      <Suspense fallback={<></>}>
                                                          <Conversation />
                                                      </Suspense>
                                                  }
                                                />
                                                
                                                <Route
                                                  path="/messages-lite/:id?"
                                                  element={
                                                      <Suspense fallback={<></>}>
                                                          <YourInbox />
                                                      </Suspense>
                                                  }
                                                />
                                                
                                            <Route
                                                path="/profile"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <Settings />
                                                    </Suspense>
                                                }
                                            />

                                                <Route element={<ProtectedRoute />}>
                                                    <Route
                                                        path="/edit-profile"
                                                        element={
                                                            <Suspense fallback={<></>}>
                                                                <EditProfile />
                                                            </Suspense>
                                                        }
                                                    />
                                                    <Route
                                                        path="/security-settings"
                                                        element={
                                                            <Suspense fallback={<></>}>
                                                                <LoginAndSecurity />
                                                            </Suspense>
                                                        }
                                                    />
                                                    <Route
                                                        path="/payment-settings"
                                                        element={
                                                            <Suspense fallback={<></>}>
                                                                <PaymentSettings />
                                                            </Suspense>
                                                        }
                                                    />
                                                    <Route
                                                        path="/payout-settings"
                                                        element={
                                                            <Suspense fallback={<></>}>
                                                                <PayoutSettings />
                                                            </Suspense>
                                                        }
                                                    />
                                                </Route>
                                            </Route>
                                            
                                            <Route
                                                path="/hospitable/init/*"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <IntegrationResult isSuccess={integrationSuccess} platform="hospitable" />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/list-your-place"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <HostLandingPage />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/book/:id/:arrivalDate/:departureDate"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <CreateBooking />
                                                    </Suspense>
                                                }
                                            />
                                            {/* <Route
                                                path="/book/:bookingID"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <CreateBooking />
                                                    </Suspense>
                                                }
                                            /> */}
                                            <Route
                                                path="/create-order/:bookingId"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <CreateOrder />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/listing/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <ViewListing />
                                                    </Suspense>
                                                }
                                            />

                                            <Route
                                                path="/search/:sessionId?"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <SearchListing />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/collection/:collectionId"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <CollectionPage />
                                                    </Suspense>
                                                }
                                            />

                                            <Route
                                                path="/privacy-policy"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <PrivacyPolicy />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/payment-policy"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <PaymentPolicy />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/terms-and-conditions"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <GeneralPolicy />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/guest/profile/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <Profile />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/host/profile/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <HostProfile />
                                                    </Suspense>
                                                }
                                            />

                                            <Route element={<ProtectedRoute />}>
                                                <Route
                                                    path="/listings/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <YourListings />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/calendar"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <YourCalendar />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/create-listing/:userhostmodelID/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <CreateListing />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/create-listing/:userhostmodelID"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <CreateListing />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/create-extra/:userhostmodelID/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <CreateExtras />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/create-extra/:userhostmodelID"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <CreateExtras />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/stay/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <BookingRequest />
                                                        </Suspense>
                                                    }
                                                />
                                            <Route
                                                path="/invoice/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <BookingHostReceipt />
                                                    </Suspense>
                                                }
                                            />
                                            
                                            <Route
                                                path="/booking/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <YourBooking />
                                                    </Suspense>
                                                }
                                            />
                                                <Route
                                                    path="/review/:bookingId/:listingId"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <ReviewStay />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/review-guest/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <ReviewGuest />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/change/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <ChangeBooking />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/inbox-lite/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <HostInbox />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/inbox"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <HostConversation />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/inbox/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <HostConversation />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/payouts"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <Payouts />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/edit-listing/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <EditListing />
                                                        </Suspense>
                                                    }
                                                />
                                            <Route
                                                path="/payments/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <ManagePayments />
                                                    </Suspense>
                                                }
                                            />
                                                <Route
                                                    path="/payout/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <PaymentDetails />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/dashboard"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <Dashboard />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/all-bookings"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <AllBookings />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/all-orders"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <AllOrders />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/extras"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <Extras />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/edit-extra/:id"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <EditExtra />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/insights"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <Insights />
                                                        </Suspense>
                                                    }
                                                />
                                                <Route
                                                    path="/tools"
                                                    element={
                                                        <Suspense fallback={<></>}>
                                                            <Tools />
                                                        </Suspense>
                                                    }
                                                />
                                            </Route>
                                            <Route
                                                path="/refer"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <ReferralPage />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/invite"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <HostReferralPage />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/google-redirect"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <GoogleRedirectPage />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/s/:slug"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <SharePage />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/dashboard"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminDashboard />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/bookings"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminBookings />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/bookings/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminBookingDetails />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/users"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminUsers />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/users/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminUserDetails />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/listings"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminListings />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/listings/:listingId"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminListingDetails />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/payments"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminPayments />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/coupons"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminCoupons />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/coupons/:id"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminCouponDetails />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/analytics"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminAnalytics />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/messages"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminMessages />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/verifications"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminVerifications />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/email"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminEmail />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="/admin/email/templates/:name"
                                                element={
                                                    <Suspense fallback={<></>}>
                                                        <AdminRouteGuard>
                                                            <AdminEmailTemplate />
                                                        </AdminRouteGuard>
                                                    </Suspense>
                                                }
                                            />
                                            <Route path="/safe-area-test" element={<SafeAreaTest />} />
                                            <Route path="*" element={<Navigate to="/" replace />} />
                                        </Routes>
                                    </SafeAreaContent>
                                    <SafeAreaBottom>
                                        {/* Bottom safe area content if needed */}
                                    </SafeAreaBottom>
                                </>
                            )}
                        </BrowserRouter>
                                </HomeScrollProvider>
                        </SafeAreaProvider>
                        </ToastProvider>
                    </RecoilRoot>
                </HelmetProvider>
            </Sentry.ErrorBoundary>
        </>
    );
}
